/**
 * Friends Page
 *
 * Features:
 * - View friends list
 * - Search for users to add
 * - Pending friend requests
 * - Friend suggestions
 * - Quick actions (message, view profile, remove friend)
 */

import {
  Search,
  UserPlus,
  UserCheck,
  Clock,
  Trash2,
  MessageSquare,
  Users,
  AlertCircle,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/cn';

type FriendsTab = 'friends' | 'requests' | 'suggestions';

interface Friend {
  id: string;
  displayName: string;
  avatar: string;
  status: 'online' | 'offline';
  lastSeen?: string;
  mutualFriends?: number;
}

interface FriendRequest {
  id: string;
  userId: string;
  displayName: string;
  avatar: string;
  sentAt: string;
}

// Mock data
const mockFriends: Friend[] = [
  {
    id: 'user1',
    displayName: 'Jordan Smith',
    avatar: 'https://picsum.photos/48/48?random=1',
    status: 'online',
    mutualFriends: 3,
  },
  {
    id: 'user2',
    displayName: 'Casey Wilson',
    avatar: 'https://picsum.photos/48/48?random=2',
    status: 'offline',
    lastSeen: '2 hours ago',
    mutualFriends: 5,
  },
  {
    id: 'user3',
    displayName: 'Sam Johnson',
    avatar: 'https://picsum.photos/48/48?random=3',
    status: 'online',
    mutualFriends: 2,
  },
  {
    id: 'user4',
    displayName: 'Morgan Davis',
    avatar: 'https://picsum.photos/48/48?random=4',
    status: 'offline',
    lastSeen: '1 day ago',
    mutualFriends: 1,
  },
];

const mockRequests: FriendRequest[] = [
  {
    id: 'req1',
    userId: 'user5',
    displayName: 'Alex Taylor',
    avatar: 'https://picsum.photos/48/48?random=5',
    sentAt: '2024-05-10',
  },
  {
    id: 'req2',
    userId: 'user6',
    displayName: 'Jordan Brown',
    avatar: 'https://picsum.photos/48/48?random=6',
    sentAt: '2024-05-09',
  },
];

const mockSuggestions: Friend[] = [
  {
    id: 'user7',
    displayName: 'Riley Martinez',
    avatar: 'https://picsum.photos/48/48?random=7',
    status: 'online',
    mutualFriends: 4,
  },
  {
    id: 'user8',
    displayName: 'Quinn Anderson',
    avatar: 'https://picsum.photos/48/48?random=8',
    status: 'offline',
    lastSeen: '3 hours ago',
    mutualFriends: 2,
  },
  {
    id: 'user9',
    displayName: 'Blake Thompson',
    avatar: 'https://picsum.photos/48/48?random=9',
    status: 'online',
    mutualFriends: 3,
  },
];

/**
 * Friends list with add, search, and request management.
 */
export function FriendsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<FriendsTab>('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [friendsList, setFriendsList] = useState<Friend[]>(mockFriends);
  const [requestsList, setRequestsList] = useState<FriendRequest[]>(mockRequests);

  // Filter friends by search
  const filteredFriends = useMemo(
    () =>
      friendsList.filter((f) =>
        f.displayName.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [friendsList, searchQuery],
  );

  const handleRemoveFriend = (userId: string) => {
    setFriendsList(friendsList.filter((f) => f.id !== userId));
    // TODO: Phase 2 - DELETE /api/friends/:userId
  };

  const handleAcceptRequest = (requestId: string, userId: string, displayName: string) => {
    setRequestsList(requestsList.filter((r) => r.id !== requestId));
    setFriendsList([
      ...friendsList,
      {
        id: userId,
        displayName,
        avatar: `https://picsum.photos/48/48?random=${Math.random()}`,
        status: 'offline',
        mutualFriends: 1,
      },
    ]);
    // TODO: Phase 2 - POST /api/friends/:requestId/accept
  };

  const handleDeclineRequest = (requestId: string) => {
    setRequestsList(requestsList.filter((r) => r.id !== requestId));
    // TODO: Phase 2 - POST /api/friends/:requestId/decline
  };

  const handleAddFriend = (userId: string) => {
    // TODO: Phase 2 - POST /api/friends/requests/:userId
    console.log('[friends] Sending friend request to', userId);
  };

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Users className="h-8 w-8 text-brand-600" />
          Friends
        </h1>
        <p className="text-slate-600 mt-1">Connect with people and collaborate on challenges</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setTab('friends')}
          className={cn(
            'pb-3 font-semibold text-sm border-b-2 transition-colors',
            tab === 'friends'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-slate-600 hover:text-slate-900',
          )}
        >
          Friends ({friendsList.length})
        </button>
        <button
          onClick={() => setTab('requests')}
          className={cn(
            'pb-3 font-semibold text-sm border-b-2 transition-colors relative',
            tab === 'requests'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-slate-600 hover:text-slate-900',
          )}
        >
          Requests
          {requestsList.length > 0 && (
            <span className="absolute -top-2 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
              {requestsList.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('suggestions')}
          className={cn(
            'pb-3 font-semibold text-sm border-b-2 transition-colors',
            tab === 'suggestions'
              ? 'border-brand-600 text-brand-600'
              : 'border-transparent text-slate-600 hover:text-slate-900',
          )}
        >
          Suggestions
        </button>
      </div>

      {/* Friends Tab */}
      {tab === 'friends' && (
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search friends..."
              className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-200"
            />
          </div>

          {/* Friends List */}
          {filteredFriends.length > 0 ? (
            <div className="space-y-2">
              {filteredFriends.map((friend) => (
                <div
                  key={friend.id}
                  className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar & Status */}
                    <div className="relative flex-shrink-0">
                      <img
                        src={friend.avatar}
                        alt={friend.displayName}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                      <div
                        className={cn(
                          'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white',
                          friend.status === 'online' ? 'bg-green-500' : 'bg-slate-400',
                        )}
                      />
                    </div>

                    {/* Name & Status */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{friend.displayName}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {friend.status === 'online' ? (
                          <span className="text-green-600">Online</span>
                        ) : (
                          <>Offline • {friend.lastSeen}</>
                        )}
                        {friend.mutualFriends && (
                          <> • {friend.mutualFriends} mutual friends</>
                        )}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => navigate(`/profile/${friend.id}`)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
                        title="View profile"
                      >
                        <Users className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveFriend(friend.id)}
                        className="p-2 rounded-lg hover:bg-red-50 transition-colors text-slate-600 hover:text-red-600"
                        title="Remove friend"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-slate-200 py-8 text-center">
              <p className="text-slate-500">
                {searchQuery ? 'No friends match your search' : "You don't have any friends yet"}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Requests Tab */}
      {tab === 'requests' && (
        <div className="space-y-4">
          {requestsList.length > 0 ? (
            <>
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">
                  You have {requestsList.length} pending friend request{requestsList.length !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="space-y-2">
                {requestsList.map((request) => (
                  <div
                    key={request.id}
                    className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <img
                        src={request.avatar}
                        alt={request.displayName}
                        className="h-12 w-12 rounded-full object-cover flex-shrink-0"
                      />

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">{request.displayName}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Sent {new Date(request.sentAt).toLocaleDateString()}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() =>
                            handleAcceptRequest(request.id, request.userId, request.displayName)
                          }
                          className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors flex items-center gap-1"
                        >
                          <UserCheck className="h-4 w-4" />
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineRequest(request.id)}
                          className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-900 text-sm font-medium hover:bg-slate-50 transition-colors"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-slate-200 py-8 text-center">
              <Clock className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-500">No pending friend requests</p>
            </div>
          )}
        </div>
      )}

      {/* Suggestions Tab */}
      {tab === 'suggestions' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            People you might know who share interests with you
          </p>

          <div className="space-y-2">
            {mockSuggestions.map((suggestion) => (
              <div
                key={suggestion.id}
                className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  {/* Avatar & Status */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={suggestion.avatar}
                      alt={suggestion.displayName}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <div
                      className={cn(
                        'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white',
                        suggestion.status === 'online' ? 'bg-green-500' : 'bg-slate-400',
                      )}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{suggestion.displayName}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {suggestion.mutualFriends} mutual friends
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/profile/${suggestion.id}`)}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-900 text-sm font-medium hover:bg-slate-50 transition-colors"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleAddFriend(suggestion.id)}
                      className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors flex items-center gap-1"
                    >
                      <UserPlus className="h-4 w-4" />
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spacer for mobile bottom nav */}
      <div className="h-8" />
    </>
  );
}
